import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BriefcaseBusiness,
  CircleDollarSign,
  Database,
  FolderOpen,
  LineChart,
  Sprout,
  Table2,
} from "lucide-react";

export interface ProjectAccent {
  name: string;
  dot: string;
  bg: string;
  softBg: string;
  border: string;
  text: string;
  ring: string;
}

export interface ProjectVisuals {
  accent: ProjectAccent;
  Icon: LucideIcon;
  tags: string[];
}

const defaultProjectAccent: ProjectAccent = {
  name: "default",
  dot: "bg-visagio-teal",
  bg: "bg-visagio-teal",
  softBg: "bg-visagio-teal/10",
  border: "border-visagio-teal/20",
  text: "text-visagio-teal",
  ring: "ring-visagio-teal/20",
};

export const projectAccents: ProjectAccent[] = [
  {
    name: "teal",
    dot: "bg-teal-500",
    bg: "bg-teal-500",
    softBg: "bg-teal-50 dark:bg-teal-950/40",
    border: "border-teal-200 dark:border-teal-800",
    text: "text-teal-700 dark:text-teal-300",
    ring: "ring-teal-500/20",
  },
  {
    name: "emerald",
    dot: "bg-emerald-500",
    bg: "bg-emerald-500",
    softBg: "bg-emerald-50 dark:bg-emerald-950/40",
    border: "border-emerald-200 dark:border-emerald-800",
    text: "text-emerald-700 dark:text-emerald-300",
    ring: "ring-emerald-500/20",
  },
  {
    name: "sky",
    dot: "bg-sky-500",
    bg: "bg-sky-500",
    softBg: "bg-sky-50 dark:bg-sky-950/40",
    border: "border-sky-200 dark:border-sky-800",
    text: "text-sky-700 dark:text-sky-300",
    ring: "ring-sky-500/20",
  },
  {
    name: "amber",
    dot: "bg-amber-500",
    bg: "bg-amber-500",
    softBg: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-200 dark:border-amber-800",
    text: "text-amber-700 dark:text-amber-300",
    ring: "ring-amber-500/20",
  },
  {
    name: "rose",
    dot: "bg-rose-500",
    bg: "bg-rose-500",
    softBg: "bg-rose-50 dark:bg-rose-950/40",
    border: "border-rose-200 dark:border-rose-800",
    text: "text-rose-700 dark:text-rose-300",
    ring: "ring-rose-500/20",
  },
  {
    name: "indigo",
    dot: "bg-indigo-500",
    bg: "bg-indigo-500",
    softBg: "bg-indigo-50 dark:bg-indigo-950/40",
    border: "border-indigo-200 dark:border-indigo-800",
    text: "text-indigo-700 dark:text-indigo-300",
    ring: "ring-indigo-500/20",
  },
];

const legacyAccentAliases: Record<string, string> = {
  primary: "sky",
  accent: "rose",
  secondary: "amber",
  brand: "teal",
  highlight: "emerald",
  neutral: "indigo",
};

export const DEFAULT_TAG_KEYS = [
  "financial", "operations", "client", "planning", "test", "data", "workspace", "indicators",
] as const;
export type DefaultTagKey = typeof DEFAULT_TAG_KEYS[number];

export function displayTag(tag: string, t: (key: string) => string): string {
  return (DEFAULT_TAG_KEYS as readonly string[]).includes(tag)
    ? t(`defaultTags.${tag}`)
    : tag;
}

export const projectIconOptions: Array<{ key: string; Icon: LucideIcon }> = [
  { key: "folder", Icon: FolderOpen },
  { key: "database", Icon: Database },
  { key: "bar-chart", Icon: BarChart3 },
  { key: "table", Icon: Table2 },
  { key: "line-chart", Icon: LineChart },
  { key: "briefcase", Icon: BriefcaseBusiness },
  { key: "finance", Icon: CircleDollarSign },
  { key: "sprout", Icon: Sprout },
];


export function getProjectVisuals(projectId: string, projectName = ""): ProjectVisuals {
  return {
    accent: defaultProjectAccent,
    Icon: FolderOpen,
    tags: [],
  };
}

export function resolveProjectVisuals(
  projectId: string,
  projectName = "",
  metadata?: {
    color?: string | null;
    icon?: string | null;
    tags?: string[] | null;
  }
): ProjectVisuals {
  const fallback = getProjectVisuals(projectId, projectName);
  const color = metadata?.color ? legacyAccentAliases[metadata.color] || metadata.color : undefined;
  const accent = projectAccents.find((item) => item.name === color) || fallback.accent;
  const Icon = projectIconOptions.find((item) => item.key === metadata?.icon)?.Icon || fallback.Icon;
  const tags = metadata?.tags?.filter(Boolean).slice(0, 6) ?? [];

  return {
    accent,
    Icon,
    tags,
  };
}
